/**
 * PendingPlanChange Domain Model
 *
 * Represents a deferred plan change scheduled via Stripe Subscription Schedule.
 * Exactly one row per billing_account at a time (DB UNIQUE constraint).
 * Cut over to the live billing_account when Stripe fires
 * `subscription_schedule.released` (see PendingPlanChangeService).
 *
 * Part of the billing bounded context. See spec §4.8 and §5.4.
 */
import { Model } from "@effect/sql"
import { Schema as S } from "effect"
import { BillingAccountId } from "@/domain/shared/branded-types"
import { CustomPricingVersionId } from "./custom-pricing-version"
import { StoredTierKey } from "./stored-tier-key"

/**
 * The `stripe_schedule_id` of a change recorded locally with no Stripe
 * schedule behind it yet (the column is NOT NULL). The deactivation reader
 * already reads `""` as "no schedule". A Teams tier downgrade is recorded this
 * way until the Teams Stripe sync installs its schedule (018 §5.1, audit 18-7):
 * the empty id is the row's "not yet synced" mark.
 */
export const UNSYNCED_STRIPE_SCHEDULE_ID = ""

/** Whether a pending row still waits for its Stripe schedule. */
export const isUnsyncedStripeSchedule = (stripeScheduleId: string): boolean =>
  stripeScheduleId === UNSYNCED_STRIPE_SCHEDULE_ID

// =============================================================================
// Branded ID Type
// =============================================================================

export const PendingPlanChangeId = S.UUID.pipe(
  S.brand("PendingPlanChangeId"),
  S.annotations({
    identifier: "PendingPlanChangeId",
    title: "Pending Plan Change ID",
    description: "UUID identifier for a deferred plan change row"
  })
)
export type PendingPlanChangeId = S.Schema.Type<typeof PendingPlanChangeId>

// =============================================================================
// Domain Entity
// =============================================================================

export class PendingPlanChange extends Model.Class<PendingPlanChange>("PendingPlanChange")({
  id: Model.Generated(PendingPlanChangeId),
  billingAccountId: BillingAccountId,

  // Any subset of target fields may be set; NULL means "no change to that aspect".
  // Any port's tier (018 §2.3, audit 18-4): the broker's `base | pro | max`, or a
  // Teams scope's tier key, as the v6.81 CHECK admits. A broker reader takes
  // the broker view (`brokerPlanKeyOf`).
  targetPlanKey: S.NullOr(StoredTierKey),
  targetSeatCount: S.NullOr(S.Int.pipe(S.greaterThanOrEqualTo(1))),
  targetBillingInterval: S.NullOr(S.Literal("month", "year")),

  targetPinnedRegionPricingVersionId: S.NullOr(CustomPricingVersionId),
  targetPinnedNetworkPricingVersionId: S.NullOr(CustomPricingVersionId),
  targetPinnedOrgPricingVersionId: S.NullOr(CustomPricingVersionId),

  stripeScheduleId: S.String,
  effectiveAt: S.DateTimeUtc,
  createdByUserId: S.String,
  correlationId: S.NullOr(S.UUID),
  createdAt: Model.DateTimeInsert
}) {}
